import React, { useCallback, useMemo } from "react";
import { useNode, useEditor, Element } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  Button,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon, ExpandMore } from "@mui/icons-material";
import { v4 as uuidv4 } from 'uuid';

export const TabControl = ({
  name,
  hidden,
  style,
  tabs = [
    { id: uuidv4(), label: "Tab 1" },
    { id: uuidv4(), label: "Tab 2" }
  ],
  activeTab = 0,
  tabSize = "",
  tabStyle = "",
  fullWidth = false,
  centered = false,
  boxed = false,
  toggle = false,
  rounded = false,
  height = "500px", // Add height prop with default
  children,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // All hooks must be called before any early returns
  const handleTabClick = useCallback((index) => {
    setProp(props => props.activeTab = index);
  }, [setProp]);

  // Build Bulma tab classes - memoize to prevent unnecessary re-calculations
  const buildTabClasses = useMemo(() => {
    const classes = ['tabs'];
    
    if (tabSize) classes.push(tabSize);
    if (tabStyle) classes.push(tabStyle);
    if (fullWidth) classes.push('is-fullwidth');
    if (centered) classes.push('is-centered');
    if (boxed) classes.push('is-boxed');
    if (toggle) classes.push('is-toggle');
    if (rounded) classes.push('is-toggle-rounded');
    
    return classes.join(' ');
  }, [tabSize, tabStyle, fullWidth, centered, boxed, toggle, rounded]);


  // Early return after all hooks
  if (hidden) return null;

  // Selection styling
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  return (
    <div
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        width: '100%',
        height: height, // Add explicit height
        display: 'flex',
        flexDirection: 'column',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {/* Tab Navigation */}
      <div className={buildTabClasses}>
        <ul>
          {tabs.map((tab, index) => (
            <li 
              key={tab.id} 
              className={activeTab === index ? 'is-active' : ''}
              onClick={() => handleTabClick(index)}
              style={{ cursor: 'pointer' }}
            >
              <a>{tab.label}</a>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Content */}
      <div 
        style={{
          flex: 1,
          minHeight: '100px',
          maxHeight: '100%',
          position: 'relative',
          overflow: 'hidden' // Prevent content overflow
        }}
      >
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            style={{
              display: activeTab === index ? 'flex' : 'none',
              height: '100%',
              flexDirection: 'column'
            }}
          >
            <Element 
              id={`tab-content-${tab.id}`} 
              is="div" 
              canvas
              style={{
                minHeight: enabled ? '50px' : '0',
                height: '100%',
                padding: enabled ? '10px' : '0',
                border: enabled ? '1px dashed #ccc' : 'none',
                borderRadius: enabled ? '4px' : '0',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Placeholder content only shows when canvas is empty and in editor mode */}
            </Element>
          </div>
        ))}
      </div>
    </div>
  );
};

const TabControlSettings = () => {
  const {
    name,
    tabs,
    activeTab,
    tabSize,
    tabStyle,
    fullWidth,
    centered,
    boxed,
    toggle,
    rounded,
    hidden,
    height,
    actions: { setProp }
  } = useNode(node => ({
    name: node.data.props.name,
    tabs: node.data.props.tabs,
    activeTab: node.data.props.activeTab,
    tabSize: node.data.props.tabSize,
    tabStyle: node.data.props.tabStyle,
    fullWidth: node.data.props.fullWidth,
    centered: node.data.props.centered,
    boxed: node.data.props.boxed,
    toggle: node.data.props.toggle,
    rounded: node.data.props.rounded,
    hidden: node.data.props.hidden,
    height: node.data.props.height
  }));

  const addTab = () => {
    setProp(props => {
      props.tabs = [...props.tabs, { id: uuidv4(), label: `Tab ${props.tabs.length + 1}` }];
    });
  };

  const removeTab = (index) => {
    setProp(props => {
      if (props.tabs.length > 1) {
        props.tabs = props.tabs.filter((_, i) => i !== index);
        // Adjust activeTab if necessary
        if (props.activeTab >= props.tabs.length - 1) {
          props.activeTab = Math.max(0, props.tabs.length - 2);
        }
      }
    });
  };

  const updateTabLabel = (index, label) => {
    setProp(props => {
      props.tabs[index].label = label;
    });
  };

  const setActiveTab = (index) => {
    setProp(props => props.activeTab = index);
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={hidden || false}
            onChange={(e) => setProp(props => props.hidden = e.target.checked)}
          />
        }
        label="Hidden"
      />

      {/* Tab Management */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Tab Management</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Tabs (Click tab name to make it active)</FormLabel>
            <Box>
              {tabs?.map((tab, index) => (
                <Box key={tab.id} display="flex" alignItems="center" gap={1} marginBottom={1}>
                  <Button
                    variant={activeTab === index ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setActiveTab(index)}
                    style={{ minWidth: '60px' }}
                  >
                    {index + 1}
                  </Button>
                  <TextField
                    size="small"
                    placeholder="Tab Label"
                    value={tab.label}
                    onChange={(e) => updateTabLabel(index, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    placeholder="UUID"
                    value={tab.id}
                    disabled
                    style={{ flex: 1, opacity: 0.6 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeTab(index)}
                    disabled={tabs.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={addTab}
                size="small"
                variant="outlined"
              >
                Add Tab
              </Button>
            </Box>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Bulma Tab Styling */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Tab Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Tab Size</FormLabel>
            <Select
              value={tabSize || ''}
              onChange={(e) => setProp(props => props.tabSize = e.target.value)}
              size="small"
            >
              <MenuItem value="">Normal</MenuItem>
              <MenuItem value="is-small">Small</MenuItem>
              <MenuItem value="is-medium">Medium</MenuItem>
              <MenuItem value="is-large">Large</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Tab Style</FormLabel>
            <Select
              value={tabStyle || ''}
              onChange={(e) => setProp(props => props.tabStyle = e.target.value)}
              size="small"
            >
              <MenuItem value="">Default</MenuItem>
              <MenuItem value="is-right">Right Aligned</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={fullWidth}
                onChange={(e) => setProp(props => props.fullWidth = e.target.checked)}
              />
            }
            label="Full Width"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={centered}
                onChange={(e) => setProp(props => props.centered = e.target.checked)}
              />
            }
            label="Centered"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={boxed}
                onChange={(e) => setProp(props => props.boxed = e.target.checked)}
              />
            }
            label="Boxed Style"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={toggle}
                onChange={(e) => setProp(props => props.toggle = e.target.checked)}
              />
            }
            label="Toggle Style"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rounded}
                onChange={(e) => setProp(props => props.rounded = e.target.checked)}
              />
            }
            label="Rounded (requires Toggle)"
          />
        </AccordionDetails>
      </Accordion>

      {/* Height Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Dimensions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            label="Height"
            value={height || "500px"}
            onChange={(e) => setProp(props => props.height = e.target.value)}
            fullWidth
            margin="normal"
            helperText="Set height (e.g., 500px, 100vh, auto)"
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export const TabControlDefaultProps = {
  name: "TabControl",
  tabs: [
    { id: uuidv4(), label: "Tab 1" },
    { id: uuidv4(), label: "Tab 2" }
  ],
  activeTab: 0,
  tabSize: "",
  tabStyle: "",
  fullWidth: false,
  centered: false,
  boxed: false,
  toggle: false,
  rounded: false,
  hidden: false,
  height: "500px"
};

TabControl.craft = {
  props: TabControlDefaultProps,
  related: {
    settings: TabControlSettings
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true
  },
  displayName: 'Tab Control'
};